/* @flow */

import JSONSerializable from 'lib/serialization/json-serializable';
import { FourNetsApi } from 'four-nets/api/api';
import { observable } from 'mobx';

import { WPAttachment } from 'lib/wp/wp-attachment';

@JSONSerializable()
class Hit {}

export { ThreadPageHit };

@JSONSerializable()
class ThreadPageHit extends Hit {
    @observable source: Object;

    fromJSON({ json }) {
        const { source, ...rest } = json;
        source.lastUpdate = new Date(source.lastUpdate);
        this.source = source;

        return super.fromJSON({ json: rest });
    }
}

export { MagazinePostHit };

@JSONSerializable()
class MagazinePostHit extends Hit {
    @observable source: Object;

    fromJSON({ json }) {
        const { source, ...rest } = json;
        source.lastUpdate = new Date(source.lastUpdate);
        source.postDateGmt = new Date(source.postDateGmt);
        this.source = source;
        super.fromJSON({ json: rest });

        if (source.featuredImage) {
            const attachment = new WPAttachment({});
            this.source.featuredImage = attachment.fromJSON({ json: source.featuredImage });
        }
    }
}

export { BazaarProductHit };

@JSONSerializable()
class BazaarProductHit extends Hit {
    @observable source: Object;

    constructor(photoFileStore) {
        super();
        this.photoFileStore = photoFileStore;
    }

    fromJSON({ json }) {
        const { source, ...rest } = json;
        source.lastUpdate = new Date(source.lastUpdate);
        this.source = source;
        super.fromJSON({ json: rest });

        if (source.featuredImage) {
            this.source.featuredImage = this.photoFileStore.processJSON({ json: source.featuredImage });
        }
    }
}

export { PhotoblogPostHit };

@JSONSerializable()
class PhotoblogPostHit extends Hit {
    @observable source: Object;

    constructor(photoFileStore) {
        super();
        this.photoFileStore = photoFileStore;
    }

    fromJSON({ json }) {
        const { source, ...rest } = json;
        source.lastUpdate = new Date(source.lastUpdate);
        this.source = source;
        super.fromJSON({ json: rest });

        if (source.photoblogTitlePhoto) {
            this.source.photoblogTitlePhoto = this.photoFileStore.processJSON({ json: source.photoblogTitlePhoto });
        }
    }
}

const KEY_TO_HIT_TYPE: {
    [key: string]: typeof Hit,
} = {
    'thread-page': ThreadPageHit,
    post: MagazinePostHit,
    'bazaar-product': BazaarProductHit,
    photoblog: PhotoblogPostHit,
};

export { SearchResult };

@JSONSerializable()
class SearchResult {
    query: Object;
    searchApi: SearchApi;

    @observable page: number;
    @observable pages: number;
    @observable result: Object = observable.array();

    loadPage: ?(options: { page: number }) => Promise<SearchResult>;

    constructor(options: { searchApi: SearchApi }) {
        const { searchApi, photoFileStore } = options;
        this.searchApi = searchApi;
        this.photoFileStore = photoFileStore;
    }

    fromJSON({ json }) {
        const { query, searchResponse } = json;

        const result = [];

        const hits = searchResponse.hits.hits.map(hitJSON => {
            const args = [];
            const hitClass = KEY_TO_HIT_TYPE[hitJSON['type']];
            if (hitClass === BazaarProductHit || hitClass === PhotoblogPostHit) {
                args.push(this.photoFileStore);
            }
            const hit = new hitClass(...args);
            return hit.fromJSON({ json: hitJSON });
        });

        hits.forEach(hit => result.push(hit));

        const from = parseInt(query.from, 10);
        const size = parseInt(query.size, 10);

        const { total } = searchResponse.hits;

        this.page = from === 0 ? 1 : Math.ceil(from / size) + 1;
        this.pages = Math.min(Math.ceil(total / size), 100);

        this.query = query;
        this.searchResponse = searchResponse;
        this.result.replace(result);

        this.loadPage = options => {
            const { page } = options;
            const request = this.searchApi.api.get({
                path: 'search',
            });

            const requestQuery = {
                ...this.query,
                from: (page - 1) * size,
            };

            request.query(requestQuery);

            return request.json().then(({ json: data }) => {
                return this.fromJSON({
                    json: {
                        query: requestQuery,
                        target: data,
                    },
                });
            });
        };
    }
}

export class SearchApi {
    api: FourNetsApi;

    constructor(options: { api: FourNetsApi }) {
        const { api } = options;
        this.api = api;
    }

    search(options: { query: string, target?: string, titleOnly?: boolean, page?: number, size?: number }) {
        const { query, target, titleOnly, size = 10, page = 1, photoFileStore, ...rest } = options;

        const requestQuery = {
            query,
            target,
            ...rest,
            size,
            from: size * (page - 1),
        };

        if (titleOnly) {
            Object.assign(requestQuery, {
                title_only: true,
            });
        }

        const request = this.api.get({
            path: 'search',
        });

        request.query(requestQuery);

        return request.json().then(({ json }) => {
            const searchResult = new SearchResult({
                searchApi: this,
                photoFileStore,
            });

            return searchResult.fromJSON({
                json: {
                    query: requestQuery,
                    searchResponse: json,
                },
            });
        });
    }
    toJSON = () => {
        return null;
    };
}
